/**
 * ItemPickerInstance[] ⇄ RecordMovingTypeIntakeRequest 변환 (server-spec aware).
 *
 * UI 의 item-picker 는 같은 itemId(이사 유형 code)에 대해 여러 인스턴스를
 * 허용한다 — 사용자 요구사항: "같은 이사유형 2개도 선택 가능".
 *
 * 한편 현재 백엔드 API(`PUT intake-session/moving-type`)의 contract 는 동일한
 * code 가 여러 번 등장할 수 없는 형태(`selectedTypes: string[]` + 단일
 * `attributesByType[code]`)다. 따라서 현재 v1 매퍼는 다음 정책을 따른다:
 *
 *   1. selectedTypes — instances 의 itemId 를 등장 순서대로 모은 뒤 unique 화.
 *   2. attributesByType[code] — 동일 code 의 **첫 인스턴스** answers 를 채택.
 *
 * ── server 직렬 string 포맷 (DynamicAttributeValidator.kt 참조) ───────────────
 *
 *   STRING                      → 그대로
 *   BOOLEAN                     → "true" | "false"
 *   INT (>= 0) / NUMBER         → 숫자 string
 *   DATE                        → "yyyy-MM-dd"
 *   DATE_RANGE                  → "yyyy-MM-dd/yyyy-MM-dd"  (slash)
 *   TIME                        → '{"hour":H,"minute":M}'  (JSON)
 *   STRUCTURED_DEPARTURE_ADDRESS → '{"roadAddress":"...","buildingName":?,
 *                                    "dong":?,"floorOrUnit":?}'
 *   ENUM                        → option value 그대로
 *   CHOICE_WITH_CUSTOM          → '{"choice":"PRESET"}'  또는
 *                                  '{"choice":"$CUSTOM","text":"..."}'
 *
 * 매퍼는 spec.attributes 의 type 정보를 받아 위 포맷을 정확히 만든다.
 */

import { isExtensibleCustomValue, type ItemPickerInstance } from '@miso/design-system';
import type {
  MovingTypeSpecificationAttribute,
  MovingTypeSpecificationResponse,
  RecordMovingTypeIntakeRequest,
} from '@/entities/visit-counsel/model/moving-type-intake.types';
import {
  resolveMovingTypeAttribute,
  type ResolvedMovingTypeAttributeType,
} from '@/features/visit-counsel/intake-form/model/resolveMovingTypeAttributeKind';
import {
  attrToQuestionId,
  CHOICE_CUSTOM_TRIGGER,
  DATE_RANGE_START_SUFFIX,
  DATE_RANGE_END_SUFFIX,
  resolveMovingTypeFlowAttributes,
} from './movingTypeSpecToFlowDocument';

const ATTR_PREFIX = 'attr__';

/** server 가 인식하는 직접입력 코드. ChoiceWithCustomAttributeValue.kt 참조. */
const SERVER_CUSTOM_CODE = '$CUSTOM';

// ── 단일 값 직렬화 헬퍼 ───────────────────────────────────────────────────────

/** "HH:MM" 또는 "H:MM" → '{"hour":H,"minute":M}' JSON. 실패 시 null. */
function encodeTime(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return JSON.stringify({ hour, minute });
}

/** start/end LocalDate 두 string 을 "yyyy-MM-dd/yyyy-MM-dd" 로 합친다. */
function encodeDateRange(start: string | undefined, end: string | undefined): string | null {
  if (!start || !end) return null;
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (!re.test(start) || !re.test(end)) return null;
  return `${start}/${end}`;
}

/**
 * choice/text 페어 → server 가 받는 ChoiceWithCustomAttributeValue JSON.
 *
 * - UI 의 trigger 코드(`CHOICE_CUSTOM_TRIGGER === 'CUSTOM'`)는 server 의
 *   `$CUSTOM` 으로 변환된다.
 * - PRESET 선택 시 text 는 보내지 않는다 (validator 가 text 비어있어야 OK).
 * - $CUSTOM 인데 text 가 비어있으면 invalid → null 반환 (caller 가 skip).
 */
function encodeChoiceWithCustom(choice: string, text: string | null | undefined): string | null {
  const isCustom = choice === CHOICE_CUSTOM_TRIGGER || choice === SERVER_CUSTOM_CODE;
  if (isCustom) {
    const t = (text ?? '').trim();
    if (!t) return null;
    return JSON.stringify({ choice: SERVER_CUSTOM_CODE, text: t });
  }
  return JSON.stringify({ choice });
}

/** 객체 또는 JSON-string → 평탄화된 record. 그 외 → null. */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('{')) return null;
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** 주소 객체에서 server 가 인식하는 키만 추려 JSON 직렬화. roadAddress 가 없으면 null. */
function encodeStructuredAddress(value: unknown): string | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const road = typeof rec.roadAddress === 'string' ? rec.roadAddress.trim() : '';
  if (!road) return null;
  const out: Record<string, unknown> = { roadAddress: road };
  if (typeof rec.buildingName === 'string' && rec.buildingName.trim()) {
    out.buildingName = rec.buildingName.trim();
  }
  if (typeof rec.dong === 'string' && rec.dong.trim()) {
    out.dong = rec.dong.trim();
  }
  // floorOrUnit 은 {type, value} 구조여야 함. server-shape 그대로 통과.
  if (rec.floorOrUnit && typeof rec.floorOrUnit === 'object') {
    out.floorOrUnit = rec.floorOrUnit;
  }
  return JSON.stringify(out);
}

// ── type 별 직렬화 ────────────────────────────────────────────────────────────

interface SerializeContext {
  answers: Record<string, unknown>;
  baseId: string;
}

/** 단일 attribute 의 값을 server 형식 string 으로 변환. 누락/invalid → null. */
function serializeAttribute(
  attr: MovingTypeSpecificationAttribute,
  ctx: SerializeContext,
  resolved: ResolvedMovingTypeAttributeType = resolveMovingTypeAttribute(attr)
): string | null {
  const baseValue = ctx.answers[ctx.baseId];

  switch (resolved.kind) {
    case 'TIME':
      return encodeTime(baseValue);

    case 'DATE_RANGE': {
      // 진행 중 sub-key 가 우선; 직렬화된 JSON({startDate,endDate}) 도 호환.
      const start = ctx.answers[`${ctx.baseId}${DATE_RANGE_START_SUFFIX}`];
      const end = ctx.answers[`${ctx.baseId}${DATE_RANGE_END_SUFFIX}`];
      if (typeof start === 'string' || typeof end === 'string') {
        return encodeDateRange(
          typeof start === 'string' ? start : undefined,
          typeof end === 'string' ? end : undefined
        );
      }
      const rec = asRecord(baseValue);
      if (!rec) return null;
      return encodeDateRange(
        typeof rec.startDate === 'string' ? rec.startDate : undefined,
        typeof rec.endDate === 'string' ? rec.endDate : undefined
      );
    }

    case 'CHOICE_WITH_CUSTOM': {
      // ExtensibleCustomSelectValue 객체.
      if (isExtensibleCustomValue(baseValue)) {
        return encodeChoiceWithCustom(baseValue.choice, baseValue.text ?? null);
      }
      // 직렬화된 JSON {choice, text}.
      const rec = asRecord(baseValue);
      if (rec && typeof rec.choice === 'string') {
        return encodeChoiceWithCustom(rec.choice, typeof rec.text === 'string' ? rec.text : null);
      }
      // 단순 string — PRESET 코드만 골랐다고 간주.
      if (typeof baseValue === 'string' && baseValue.trim()) {
        return encodeChoiceWithCustom(baseValue.trim(), null);
      }
      return null;
    }

    case 'STRUCTURED_DEPARTURE_ADDRESS':
      return encodeStructuredAddress(baseValue);

    case 'BOOLEAN':
      if (typeof baseValue === 'boolean') return baseValue ? 'true' : 'false';
      if (typeof baseValue === 'string') {
        const v = baseValue.trim().toLowerCase();
        if (v === 'true' || v === 'false') return v;
      }
      return null;

    case 'INT':
    case 'NUMBER':
      if (typeof baseValue === 'number' && Number.isFinite(baseValue)) {
        return String(baseValue);
      }
      if (typeof baseValue === 'string' && baseValue.trim()) {
        return baseValue.trim();
      }
      return null;

    case 'DATE':
    case 'ENUM':
    case 'STRING':
    default:
      if (typeof baseValue === 'string' && baseValue.trim()) {
        return baseValue;
      }
      if (typeof baseValue === 'number' || typeof baseValue === 'boolean') {
        return String(baseValue);
      }
      return null;
  }
}

/**
 * 한 instance 의 answers 를 spec.attributes 순회로 server 형식 map 으로 변환.
 * spec 에 정의되지 않은 추가 답변은 무시 (server validator 가 unknown key 를
 * 검사하지는 않지만, 의도된 attribute 만 보내는 것이 안전).
 */
function answersToAttributes(
  answers: Record<string, unknown>,
  spec: MovingTypeSpecificationResponse
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const attr of resolveMovingTypeFlowAttributes(spec)) {
    const baseId = attrToQuestionId(attr.key);
    const resolved = resolveMovingTypeAttribute(attr);
    const value = serializeAttribute(attr, { answers, baseId }, resolved);
    if (value == null) continue;
    out[attr.key] = value;
  }
  return out;
}

// ── Public API ───────────────────────────────────────────────────────────────

export interface BuildIntakeOptions {
  specs?: MovingTypeSpecificationResponse[] | null;
}

/**
 * instances 와 specs 를 받아 server contract 로 변환한다.
 * specs 가 비어있거나 매칭되는 spec 이 없으면 해당 code 의 attributesByType 는
 * 빈 객체로 둔다 (선택 자체는 보존).
 */
export function instancesToMovingTypeIntake(
  instances: ItemPickerInstance[] | undefined,
  options: BuildIntakeOptions = {}
): RecordMovingTypeIntakeRequest {
  const specs = options.specs ?? [];
  const specByCode = new Map(specs.map((s) => [s.code, s]));

  const selectedTypes: string[] = [];
  const attributesByType: Record<string, Record<string, string>> = {};

  for (const inst of instances ?? []) {
    const code = inst.itemId;
    if (!code) continue;
    if (selectedTypes.includes(code)) continue;
    selectedTypes.push(code);
    const spec = specByCode.get(code);
    attributesByType[code] = spec ? answersToAttributes(inst.answers ?? {}, spec) : {};
  }

  return { selectedTypes, attributesByType };
}

// ── 역변환 (prefill) ──────────────────────────────────────────────────────────

/**
 * 단일 attribute 의 server-string 을 InquiryFlow answers 형식으로 deserialize.
 * 결과는 answers 에 추가되어야 할 [questionId, value] 쌍 목록.
 */
function deserializeAttribute(
  attr: MovingTypeSpecificationAttribute,
  raw: string,
  resolved: ResolvedMovingTypeAttributeType
): Array<[string, unknown]> {
  const baseId = attrToQuestionId(attr.key);

  switch (resolved.kind) {
    case 'TIME': {
      try {
        const parsed = JSON.parse(raw) as { hour?: number; minute?: number };
        if (typeof parsed?.hour === 'number' && typeof parsed?.minute === 'number') {
          const hh = String(parsed.hour).padStart(2, '0');
          const mm = String(parsed.minute).padStart(2, '0');
          return [[baseId, `${hh}:${mm}`]];
        }
      } catch {
        // fall through
      }
      return [[baseId, raw]];
    }

    case 'DATE_RANGE': {
      const slash = raw.split('/');
      if (slash.length === 2) {
        return [
          [`${baseId}${DATE_RANGE_START_SUFFIX}`, slash[0]],
          [`${baseId}${DATE_RANGE_END_SUFFIX}`, slash[1]],
        ];
      }
      // 옛 JSON {startDate, endDate} 호환
      try {
        const parsed = JSON.parse(raw) as { startDate?: string; endDate?: string };
        const out: Array<[string, unknown]> = [];
        if (typeof parsed?.startDate === 'string') {
          out.push([`${baseId}${DATE_RANGE_START_SUFFIX}`, parsed.startDate]);
        }
        if (typeof parsed?.endDate === 'string') {
          out.push([`${baseId}${DATE_RANGE_END_SUFFIX}`, parsed.endDate]);
        }
        if (out.length > 0) return out;
      } catch {
        // ignore
      }
      return [[baseId, raw]];
    }

    case 'CHOICE_WITH_CUSTOM': {
      try {
        const parsed = JSON.parse(raw) as { choice?: string; text?: string };
        if (typeof parsed?.choice === 'string') {
          // server 의 $CUSTOM → UI 의 trigger value 로 normalize.
          const isCustom = parsed.choice === SERVER_CUSTOM_CODE;
          return [
            [
              baseId,
              {
                choice: isCustom ? CHOICE_CUSTOM_TRIGGER : parsed.choice,
                text: typeof parsed.text === 'string' ? parsed.text : '',
              },
            ],
          ];
        }
      } catch {
        // fall through
      }
      return [[baseId, raw]];
    }

    case 'STRUCTURED_DEPARTURE_ADDRESS': {
      try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        return [[baseId, parsed]];
      } catch {
        return [[baseId, raw]];
      }
    }

    case 'BOOLEAN':
      return [[baseId, raw === 'true']];

    case 'INT':
    case 'NUMBER': {
      const n = Number(raw);
      return [[baseId, Number.isFinite(n) ? n : raw]];
    }

    case 'DATE':
    case 'ENUM':
    case 'STRING':
    default:
      return [[baseId, raw]];
  }
}

/**
 * 기존 movingTypeIntake → ItemPickerInstance[] 역변환 (편집 prefill 용).
 * spec 이 주어지면 type 별 정확한 deserialize, 없으면 raw string 그대로.
 */
export function movingTypeIntakeToInstances(
  intake:
    | {
        selectedTypes?: string[] | null;
        attributesByType?: Record<string, Record<string, string>> | null;
      }
    | null
    | undefined,
  options: BuildIntakeOptions = {}
): ItemPickerInstance[] {
  if (!intake) return [];
  const specs = options.specs ?? [];
  const specByCode = new Map(specs.map((s) => [s.code, s]));

  const types = intake.selectedTypes ?? [];
  const byType = intake.attributesByType ?? {};
  return types.map((code, idx) => {
    const attrs = byType[code] ?? {};
    const spec = specByCode.get(code);
    const answers: Record<string, unknown> = {};
    if (spec) {
      const attrByKey = new Map(resolveMovingTypeFlowAttributes(spec).map((a) => [a.key, a]));
      for (const [k, v] of Object.entries(attrs)) {
        const attr = attrByKey.get(k);
        if (!attr) {
          answers[attrToQuestionId(k)] = v;
          continue;
        }
        const resolved = resolveMovingTypeAttribute(attr);
        for (const [qid, val] of deserializeAttribute(attr, v, resolved)) {
          answers[qid] = val;
        }
      }
    } else {
      // spec 미지정 시 옛 동작 (lossy fallback) — 객체 JSON 만 풀어준다.
      for (const [k, v] of Object.entries(attrs)) {
        if (typeof v === 'string' && v.startsWith('{') && v.endsWith('}')) {
          try {
            const parsed = JSON.parse(v) as Record<string, unknown>;
            if ('startDate' in parsed || 'endDate' in parsed) {
              const baseId = attrToQuestionId(k);
              if (typeof parsed.startDate === 'string') {
                answers[`${baseId}${DATE_RANGE_START_SUFFIX}`] = parsed.startDate;
              }
              if (typeof parsed.endDate === 'string') {
                answers[`${baseId}${DATE_RANGE_END_SUFFIX}`] = parsed.endDate;
              }
              continue;
            }
            answers[attrToQuestionId(k)] = parsed;
            continue;
          } catch {
            // ignore
          }
        }
        answers[attrToQuestionId(k)] = v;
      }
    }
    return {
      instanceId: `restored__${code}__${idx}`,
      itemId: code,
      answers,
    };
  });
}
